import { Box, Typography, SxProps, Theme } from '@mui/material';

export type QuestionnaireProgressTabProps = Readonly<{
  title: string;
  subcategories: {
    id: string;
    label: string;
  }[];
  highlighted: boolean;
  active: boolean;
  activeSubcategoryId?: string;
  onSubcategoryClick?: (subcategoryId: string) => void;
  onTabClick?: () => void;
  showSubcategories?: boolean;
}>;

export const QuestionnaireProgressTab = ({
  title,
  subcategories,
  highlighted,
  active,
  activeSubcategoryId,
  onSubcategoryClick,
  onTabClick,
  showSubcategories = true,
}: QuestionnaireProgressTabProps) => {
  return (
    <Box sx={containerSx}>
      <Box sx={barContainerSx} onClick={onTabClick}>
        <Box
          sx={barSx(showSubcategories ? highlighted : false, showSubcategories ? active : false)}
        />
        <Typography
          variant="text-sm-semibold"
          sx={titleSx(showSubcategories ? activeSubcategoryId : undefined)}
        >
          {title}
        </Typography>
      </Box>

      {showSubcategories && subcategories.length > 1 && (
        <Box sx={subcategoriesContainerSx}>
          {subcategories.map((subcategory) => (
            <Typography
              key={subcategory.id}
              variant="text-sm-regular"
              sx={subcategoryLabelSx(subcategory.id === activeSubcategoryId)}
              onClick={() => onSubcategoryClick?.(subcategory.id)}
            >
              {subcategory.label}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
};

const barContainerSx: SxProps<Theme> = {
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
};

const barSx =
  (highlighted: boolean, active: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '100%',
    height: '4px',
    marginBottom: backrs.spacing['spacing-md'],
    backgroundColor: active
      ? backrs.colors['Colors/Text/text-quaternary-on-brand']
      : highlighted
        ? backrs.colors['Colors/Foreground/fg-brand-primary-alt']
        : backrs.colors['Colors/Background/bg-quaternary'],
  });

const titleSx =
  (activeSubcategoryId: string | undefined): SxProps<Theme> =>
  ({ backrs }) => ({
    color: activeSubcategoryId
      ? backrs.colors['Colors/Text/text-brand-secondary']
      : backrs.colors['Colors/Text/text-secondary'],
  });

const subcategoriesContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const subcategoryLabelSx =
  (isActive: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    cursor: 'pointer',
    transition: 'color 0.2s ease-in-out',
    color: isActive
      ? backrs.colors['Colors/Text/text-brand-secondary']
      : backrs.colors['Colors/Text/text-placeholder'],
    userSelect: 'none',
  });
